import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useLocalization } from "@fluent/react";
import { Row, Button, Alert } from "@narmi/design_system";
import { useNotificationContext } from "cerulean";
import { parseErrorResponse } from "byzantine/src/dbbl/composites";
import {
  getDataRowPreview,
  resolveColumnMappings,
} from "byzantine/src/dbbl/businessLogic/entities/csvMapping/utils";
import Container from "../../Container";
import FileInfoCard from "./FileInfoCard";
import MappingTable, { type FieldConfig } from "./MappingTable";
import SavedMappingToolbar from "./SavedMappingToolbar";
import SaveMappingModal from "./SaveMappingModal";
import styles from "./ColumnMappingScreen.module.scss";

// Minimal shapes the base component needs from the domain types.
// ACH and Wire concrete types satisfy these via structural compatibility.
type ColumnMappingBase = {
  field: string;
  column_index: number;
  column_header: string | null;
};

type SavedMappingBase = {
  id: string;
  name: string;
  has_header_row: boolean;
};

type CsvResponseBase = {
  original_filename?: string;
  organization_file_uuid: string;
  s3_key: string;
  columns: string[];
  header_row: string[] | null;
  preview_rows: string[][];
  row_count: number;
  suggested_mapping: {
    mapping_uuid: string | null;
    column_mappings: ColumnMappingBase[];
  };
};

type ParseResponseBase = {
  threshold_warning_fields?: string[];
  warnings?: string[];
  warning_context?: Record<string, Record<string, string | number>>;
};

type MappingHooks<
  F extends string,
  CM extends ColumnMappingBase & { field: F },
  SM extends SavedMappingBase,
  PR extends ParseResponseBase,
  RQ = unknown
> = {
  useFetchMappings: () => {
    send: () => Promise<unknown>;
    data: unknown;
    loading: boolean;
  };
  useCreateMapping: () => {
    createMapping: (payload: RQ) => Promise<void> | Promise<SM>;
    loading: boolean;
  };
  useUpdateMapping: () => {
    updateMapping: (id: string, payload: RQ) => Promise<void> | Promise<SM>;
    loading: boolean;
  };
  useParseCSV: () => {
    parseCSV: (payload: {
      s3_key: string;
      has_header_row: boolean;
      column_mappings: CM[];
    }) => Promise<void> | Promise<PR>;
    loading: boolean;
  };
  savedMappingToColumnMappings: (mapping: SM) => CM[];
  columnMappingsToSavedMapping: (
    name: string,
    hasHeaderRow: boolean,
    mappings: CM[]
  ) => RQ;
  getMissingRequiredFields: (mappings: CM[]) => F[];
  extractSavedMappings: (data: unknown) => SM[];
};

export interface ColumnMappingState<CM, SM> {
  organizationFileUuid: string;
  hasHeaderRow: boolean;
  columnMappings: CM[];
  selectedSavedMapping: SM | null;
  initialMappingsSnapshot: CM[];
  initialHasHeaderRow: boolean;
}

export type ColumnMappingScreenProps<
  F extends string,
  CM extends ColumnMappingBase & { field: F },
  SM extends SavedMappingBase,
  PR extends ParseResponseBase,
  RQ = unknown
> = {
  mappingState?: ColumnMappingState<CM, SM> | null;
  onMappingStateChange?: (state: ColumnMappingState<CM, SM>) => void;
  titleKey: string;
  backPath: string;
  fields: FieldConfig<F>[];
  csvResponse: CsvResponseBase | null;
  hooks: MappingHooks<F, CM, SM, PR, RQ>;
  parseWarningFields?: Partial<Record<string, F>>;
  parseWarningContextOverrides?: Partial<
    Record<string, Record<string, string | number>>
  >;
  onParseSuccess: (result: PR) => void;
};

const areMappingsEqual = (
  a: ColumnMappingBase[],
  b: ColumnMappingBase[]
): boolean => {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort((x, y) => x.field.localeCompare(y.field));
  const sortedB = [...b].sort((x, y) => x.field.localeCompare(y.field));
  return sortedA.every(
    (mapping, i) =>
      mapping.field === sortedB[i].field &&
      mapping.column_index === sortedB[i].column_index
  );
};

const ColumnMappingScreen = <
  F extends string,
  CM extends ColumnMappingBase & { field: F },
  SM extends SavedMappingBase,
  PR extends ParseResponseBase,
  RQ = unknown
>({
  mappingState,
  onMappingStateChange,
  titleKey,
  backPath,
  fields,
  csvResponse,
  hooks,
  parseWarningFields = {},
  parseWarningContextOverrides = {},
  onParseSuccess,
}: ColumnMappingScreenProps<F, CM, SM, PR, RQ>) => {
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const { sendNotification } = useNotificationContext();

  const {
    useFetchMappings,
    useCreateMapping,
    useUpdateMapping,
    useParseCSV,
    savedMappingToColumnMappings,
    columnMappingsToSavedMapping,
    getMissingRequiredFields,
    extractSavedMappings,
  } = hooks;

  const {
    send: fetchMappings,
    data: mappingsData,
    loading: mappingsLoading,
  } = useFetchMappings();
  const { createMapping, loading: createLoading } = useCreateMapping();
  const { updateMapping, loading: updateLoading } = useUpdateMapping();
  const { parseCSV, loading: parseLoading } = useParseCSV();

  const restoredState =
    mappingState?.organizationFileUuid === csvResponse?.organization_file_uuid
      ? mappingState
      : null;
  const mappingRevision = useRef(0);
  useEffect(
    () => () => {
      mappingRevision.current += 1;
    },
    []
  );
  const [hasHeaderRow, setHasHeaderRow] = useState(
    restoredState?.hasHeaderRow ?? true
  );
  const [columnMappings, setColumnMappings] = useState<CM[]>(
    restoredState?.columnMappings ?? []
  );
  const [selectedSavedMapping, setSelectedSavedMapping] = useState<SM | null>(
    restoredState?.selectedSavedMapping ?? null
  );
  const [initialMappingsSnapshot, setInitialMappingsSnapshot] = useState<CM[]>(
    restoredState?.initialMappingsSnapshot ?? []
  );
  const [initialHasHeaderRow, setInitialHasHeaderRow] = useState(
    restoredState?.initialHasHeaderRow ?? true
  );
  const [previewRowIndex, setPreviewRowIndex] = useState(0);
  const [saveMappingModalOpen, setSaveMappingModalOpen] = useState(false);
  const [showRequiredError, setShowRequiredError] = useState(false);
  const previousOrganizationFileUuidRef = useRef<string | null>(
    restoredState?.organizationFileUuid ?? null
  );
  const autoSelectedMappingFileUuidRef = useRef<string | null>(
    restoredState?.organizationFileUuid ?? null
  );
  const [thresholdWarningFields, setThresholdWarningFields] = useState<F[]>([]);
  const [parseWarnings, setParseWarnings] = useState<string[]>([]);
  const [parseWarningContext, setParseWarningContext] = useState<
    Record<string, Record<string, string | number>>
  >({});
  const [parseError, setParseError] = useState<string | null>(null);
  const [pendingParsedCsvResponse, setPendingParsedCsvResponse] =
    useState<PR | null>(null);

  // Redirect back to upload if no CSV data in context
  useEffect(() => {
    if (!csvResponse) {
      navigate(backPath, { replace: true });
    }
  }, [csvResponse, navigate, backPath]);

  // Initialize/reset state when the uploaded file changes
  useEffect(() => {
    const currentOrganizationFileUuid =
      csvResponse?.organization_file_uuid ?? null;
    if (
      previousOrganizationFileUuidRef.current === currentOrganizationFileUuid
    ) {
      return;
    }

    mappingRevision.current += 1;
    previousOrganizationFileUuidRef.current = currentOrganizationFileUuid;
    setParseError(null);

    if (csvResponse?.suggested_mapping) {
      const suggested = csvResponse.suggested_mapping.column_mappings as CM[];
      setColumnMappings(suggested);
    } else {
      setColumnMappings([]);
    }
    setSelectedSavedMapping(null);
    setInitialMappingsSnapshot([]);
    setHasHeaderRow(true);
    setInitialHasHeaderRow(true);
    setPreviewRowIndex(0);
    autoSelectedMappingFileUuidRef.current = null;
    setThresholdWarningFields([]);
    setParseWarnings([]);
    setPendingParsedCsvResponse(null);
  }, [csvResponse]);

  useEffect(() => {
    if (!csvResponse) return;
    onMappingStateChange?.({
      organizationFileUuid: csvResponse.organization_file_uuid,
      hasHeaderRow,
      columnMappings,
      selectedSavedMapping,
      initialMappingsSnapshot,
      initialHasHeaderRow,
    });
  }, [
    csvResponse,
    hasHeaderRow,
    columnMappings,
    selectedSavedMapping,
    initialMappingsSnapshot,
    initialHasHeaderRow,
    onMappingStateChange,
  ]);

  useEffect(() => {
    fetchMappings();
  }, [fetchMappings]);

  const savedMappings = useMemo(
    () => extractSavedMappings(mappingsData),
    [mappingsData, extractSavedMappings]
  );

  const existingNames = useMemo(
    () => savedMappings.map((m) => m.name),
    [savedMappings]
  );

  const hasUnsavedChanges = useMemo(() => {
    if (!selectedSavedMapping) return false;
    if (hasHeaderRow !== initialHasHeaderRow) return true;
    return !areMappingsEqual(columnMappings, initialMappingsSnapshot);
  }, [
    selectedSavedMapping,
    columnMappings,
    initialMappingsSnapshot,
    hasHeaderRow,
    initialHasHeaderRow,
  ]);

  const missingRequiredFields = useMemo(
    () => getMissingRequiredFields(columnMappings),
    [columnMappings, getMissingRequiredFields]
  );

  const highlightedWarningFields = useMemo(() => {
    const highlightedParseWarningFields = parseWarnings
      .map((warningType) => parseWarningFields[warningType])
      .filter((field): field is F => Boolean(field));

    return [
      ...new Set([...thresholdWarningFields, ...highlightedParseWarningFields]),
    ];
  }, [parseWarnings, parseWarningFields, thresholdWarningFields]);

  const columns = csvResponse?.columns ?? [];
  const { previewRows, rowCount } = getDataRowPreview({
    headerRow: csvResponse?.header_row ?? null,
    previewRows: csvResponse?.preview_rows ?? [],
    rowCount: csvResponse?.row_count ?? 0,
    hasHeaderRow,
  });
  const fileName =
    csvResponse?.original_filename ||
    l10n.getString("column-mapping-uploaded-csv", null, "Uploaded CSV");

  const handleMappingChange = (field: F, columnIndex: number | null) => {
    mappingRevision.current += 1;
    setShowRequiredError(false);
    setParseError(null);
    setThresholdWarningFields([]);
    setParseWarnings([]);
    setPendingParsedCsvResponse(null);
    setColumnMappings((prev) => {
      const filtered = prev.filter((cm) => cm.field !== field);
      if (columnIndex === null) return filtered;
      return [
        ...filtered,
        {
          field,
          column_index: columnIndex,
          column_header:
            hasHeaderRow && columns[columnIndex] ? columns[columnIndex] : null,
        } as CM,
      ];
    });
  };

  const handleToggleHeaderRow = (hasHeader: boolean) => {
    mappingRevision.current += 1;
    setHasHeaderRow(hasHeader);
    setPreviewRowIndex(0);
    setParseError(null);
    setThresholdWarningFields([]);
    setParseWarnings([]);
    setPendingParsedCsvResponse(null);
    setColumnMappings((prev) =>
      prev.map(
        (cm) =>
          ({
            ...cm,
            column_header:
              hasHeader && columns[cm.column_index]
                ? columns[cm.column_index]
                : null,
          } as CM)
      )
    );
  };

  const handleSelectSavedMapping = useCallback(
    (mapping: SM) => {
      mappingRevision.current += 1;
      const converted = resolveColumnMappings(
        savedMappingToColumnMappings(mapping),
        csvResponse?.columns ?? [],
        mapping.has_header_row
      );
      setSelectedSavedMapping(mapping);
      setColumnMappings(converted);
      setInitialMappingsSnapshot(converted);
      setHasHeaderRow(mapping.has_header_row);
      setInitialHasHeaderRow(mapping.has_header_row);
      setPreviewRowIndex(0);
      setShowRequiredError(false);
      setParseError(null);
      setThresholdWarningFields([]);
      setParseWarnings([]);
      setPendingParsedCsvResponse(null);
    },
    [savedMappingToColumnMappings, csvResponse]
  );

  const handleClearSavedMapping = useCallback(() => {
    mappingRevision.current += 1;
    setSelectedSavedMapping(null);
    setColumnMappings([]);
    setInitialMappingsSnapshot([]);
    setInitialHasHeaderRow(hasHeaderRow);
    setPreviewRowIndex(0);
    setShowRequiredError(false);
    setParseError(null);
    setThresholdWarningFields([]);
    setParseWarnings([]);
    setPendingParsedCsvResponse(null);
    // Don't let the suggested mapping re-apply itself after an explicit clear.
    autoSelectedMappingFileUuidRef.current =
      csvResponse?.organization_file_uuid ?? null;
  }, [hasHeaderRow, csvResponse]);

  useEffect(() => {
    const suggestedMappingUuid = csvResponse?.suggested_mapping.mapping_uuid;
    const organizationFileUuid = csvResponse?.organization_file_uuid;

    if (!suggestedMappingUuid || !organizationFileUuid) return;
    if (autoSelectedMappingFileUuidRef.current === organizationFileUuid) {
      return;
    }

    const matchedMapping = savedMappings.find(
      (mapping) => mapping.id === suggestedMappingUuid
    );
    if (matchedMapping) {
      mappingRevision.current += 1;
      const resolvedMappings = csvResponse.suggested_mapping
        .column_mappings as CM[];
      setSelectedSavedMapping(matchedMapping);
      setColumnMappings(resolvedMappings);
      setInitialMappingsSnapshot(resolvedMappings);
      setHasHeaderRow(matchedMapping.has_header_row);
      setInitialHasHeaderRow(matchedMapping.has_header_row);
      setPreviewRowIndex(0);
      setShowRequiredError(false);
      setParseError(null);
      setParseWarnings([]);
      setThresholdWarningFields([]);
      setPendingParsedCsvResponse(null);
      autoSelectedMappingFileUuidRef.current = organizationFileUuid;
    }
  }, [csvResponse, savedMappings]);

  const handleSaveNewMapping = useCallback(
    async (name: string) => {
      try {
        const payload = columnMappingsToSavedMapping(
          name,
          hasHeaderRow,
          columnMappings
        );
        const result = await createMapping(payload);
        if (!result) return;

        sendNotification({
          type: "success",
          text: l10n.getString("column-mapping-mapping-saved-toast"),
        });
        setSaveMappingModalOpen(false);
        await fetchMappings();
        setSelectedSavedMapping(result);
        setInitialMappingsSnapshot([...columnMappings]);
        setInitialHasHeaderRow(hasHeaderRow);
      } catch {
        // Error toast is handled in the request hook.
      }
    },
    [
      hasHeaderRow,
      columnMappings,
      columnMappingsToSavedMapping,
      createMapping,
      sendNotification,
      l10n,
      fetchMappings,
    ]
  );

  const handleUpdateMapping = useCallback(async () => {
    if (!selectedSavedMapping) return;

    try {
      const payload = columnMappingsToSavedMapping(
        selectedSavedMapping.name,
        hasHeaderRow,
        columnMappings
      );
      const result = await updateMapping(selectedSavedMapping.id, payload);
      if (!result) return;

      sendNotification({
        type: "success",
        text: l10n.getString("column-mapping-mapping-updated-toast"),
      });
      setInitialMappingsSnapshot([...columnMappings]);
      setInitialHasHeaderRow(hasHeaderRow);
      await fetchMappings();
    } catch {
      // Error toast is handled in the request hook.
    }
  }, [
    selectedSavedMapping,
    hasHeaderRow,
    columnMappings,
    columnMappingsToSavedMapping,
    updateMapping,
    sendNotification,
    l10n,
    fetchMappings,
  ]);

  if (!csvResponse) {
    return null;
  }

  const handleClickNext = async () => {
    if (missingRequiredFields.length > 0) {
      setShowRequiredError(true);
      return;
    }

    // Second click after warnings were shown — proceed with stored result
    if (pendingParsedCsvResponse) {
      onParseSuccess(pendingParsedCsvResponse);
      return;
    }

    mappingRevision.current += 1;
    const revision = mappingRevision.current;
    try {
      setParseError(null);
      const result = await parseCSV({
        s3_key: csvResponse.s3_key,
        has_header_row: hasHeaderRow,
        column_mappings: columnMappings,
      });
      if (!result || revision !== mappingRevision.current) return;

      const warningFields = (result.threshold_warning_fields ?? []) as F[];
      const warnings = result.warnings ?? [];
      const hasWarnings = warningFields.length > 0 || warnings.length > 0;

      if (hasWarnings) {
        setThresholdWarningFields(warningFields);
        setParseWarnings(warnings);
        setParseWarningContext(result.warning_context ?? {});
        setPendingParsedCsvResponse(result);
        return;
      }

      onParseSuccess(result);
    } catch (error) {
      const { errors } = await parseErrorResponse(error as never);
      if (revision !== mappingRevision.current) return;
      setParseError(
        errors[0]?.description ?? l10n.getString("column-mapping-generic-error")
      );
    }
  };

  return (
    <Container maxWidth="960px">
      <h1 className="margin--top--xxl">{l10n.getString(titleKey)}</h1>
      <div className="margin--top--xl">
        <FileInfoCard
          fileName={fileName}
          columnCount={columns.length}
          rowCount={rowCount}
          hasHeaderRow={hasHeaderRow}
          onToggleHeaderRow={handleToggleHeaderRow}
        />
      </div>

      {parseError && (
        <div className={`margin--top--xl ${styles.thresholdWarningAlert}`}>
          <Alert isActive kind="error" isDismissable={false}>
            {parseError}
          </Alert>
        </div>
      )}

      {thresholdWarningFields.length > 0 && (
        <div className={`margin--top--xl ${styles.thresholdWarningAlert}`}>
          <Alert isActive kind="warn" isDismissable={false}>
            {l10n.getString("column-mapping-value-warning")}
          </Alert>
        </div>
      )}

      {parseWarnings.map((warningType, index) => (
        <div
          key={warningType}
          className={`${
            index === 0 && thresholdWarningFields.length === 0
              ? "margin--top--xl"
              : "margin--top--l"
          } ${styles.thresholdWarningAlert}`}
          data-testid={`parse-warning-${warningType}`}
        >
          <Alert isActive kind="warn" isDismissable={false}>
            {l10n.getString(
              `column-mapping-warning-${warningType.replace(/_/g, "-")}`,
              {
                ...(parseWarningContext[warningType] ?? {}),
                ...(parseWarningContextOverrides[warningType] ?? {}),
              }
            )}
          </Alert>
        </div>
      ))}

      <div className="margin--top--xl">
        <div className={styles.fieldMappingHeader}>
          <span className="fontSize--l fontWeight--bold">
            {l10n.getString("column-mapping-field-mapping-heading")}
          </span>
          <span className={styles.fieldMappingDescription}>
            {l10n.getString("column-mapping-field-mapping-description")}
          </span>
        </div>

        <div className="margin--top--l">
          <SavedMappingToolbar
            savedMappings={savedMappings}
            selectedMapping={selectedSavedMapping}
            hasUnsavedChanges={hasUnsavedChanges}
            canSaveNew={columnMappings.length > 0}
            onSelectMapping={handleSelectSavedMapping}
            onClearMapping={handleClearSavedMapping}
            onSaveNew={() => setSaveMappingModalOpen(true)}
            onUpdateMapping={handleUpdateMapping}
            updateLoading={updateLoading}
            mappingsLoading={mappingsLoading}
          />
        </div>

        {showRequiredError && (
          <div className={`margin--top--m ${styles.requiredFieldsAlert}`}>
            <Alert isActive kind="error" isDismissable={false}>
              {l10n.getString("column-mapping-required-fields-error")}
            </Alert>
          </div>
        )}

        <div className="margin--top--m">
          <MappingTable
            fields={fields}
            columnMappings={columnMappings}
            columns={columns}
            previewRows={previewRows}
            hasHeaderRow={hasHeaderRow}
            previewRowIndex={previewRowIndex}
            onPreviewRowChange={setPreviewRowIndex}
            onMappingChange={handleMappingChange}
            errorFields={showRequiredError ? missingRequiredFields : []}
            warningFields={highlightedWarningFields}
          />
        </div>
      </div>

      <div className="margin--bottom--xl margin--top--xl">
        <Row alignItems="center" justifyContent="end">
          <Row.Item shrink>
            <Button
              kind="negative"
              label={l10n.getString("button-back")}
              onClick={() => navigate(backPath)}
            />
          </Row.Item>
          <Row.Item shrink>
            <Button
              label={l10n.getString("button-next")}
              onClick={handleClickNext}
              isLoading={parseLoading}
            />
          </Row.Item>
        </Row>
      </div>

      <SaveMappingModal
        isOpen={saveMappingModalOpen}
        onClose={() => setSaveMappingModalOpen(false)}
        onSave={handleSaveNewMapping}
        existingNames={existingNames}
        loading={createLoading}
      />
    </Container>
  );
};

export default ColumnMappingScreen;
